import React, { useState } from 'react';
import { Copy, Download, RotateCcw, Code2, Check } from 'lucide-react';

const TypeScriptFormatter = () => {
  const [inputCode, setInputCode] = useState('');
  const [formattedCode, setFormattedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [indentSize, setIndentSize] = useState(2);
  const [useTabs, setUseTabs] = useState(false);

  const formatTypeScript = (code: any) => {
    if (!code.trim()) {
      setFormattedCode('');
      return;
    }

    try {
      // Basic TypeScript formatting logic
      let formatted = code;
      
      // Remove extra whitespace
      formatted = formatted.replace(/\s+/g, ' ');
      
      // Handle braces and indentation
      let indent = 0;
      let result = '';
      let inString = false;
      let stringChar = '';
      
      for (let i = 0; i < formatted.length; i++) {
        const char = formatted[i];
        const nextChar = formatted[i + 1];
        const prevChar = formatted[i - 1];
        
        // Handle string literals
        if ((char === '"' || char === "'" || char === '`') && prevChar !== '\\') {
          if (!inString) {
            inString = true;
            stringChar = char;
          } else if (char === stringChar) {
            inString = false;
            stringChar = '';
          }
        }
        
        if (!inString) {
          if (char === '{') {
            result += char;
            if (nextChar !== '}') {
              result += '\n';
              indent++;
              result += getIndentation(indent, indentSize, useTabs);
            }
          } else if (char === '}') {
            if (prevChar !== '{') {
              result = result.trim();
              result += '\n';
              indent--;
              result += getIndentation(indent, indentSize, useTabs);
            }
            result += char;
            if (nextChar && nextChar !== ',' && nextChar !== ';' && nextChar !== '}') {
              result += '\n';
              result += getIndentation(indent, indentSize, useTabs);
            }
          } else if (char === ';') {
            result += char;
            if (nextChar && nextChar !== '}') {
              result += '\n';
              result += getIndentation(indent, indentSize, useTabs);
            }
          } else if (char === ',') {
            result += char;
            if (nextChar && nextChar !== '}' && nextChar !== ']') {
              result += '\n';
              result += getIndentation(indent, indentSize, useTabs);
            }
          } else {
            result += char;
          }
        } else {
          result += char;
        }
      }
      
      // Clean up extra newlines and spaces
      result = result.replace(/\n\s*\n/g, '\n');
      result = result.replace(/^\s+/gm, (match, offset) => {
        const lineStart = result.lastIndexOf('\n', offset - 1) + 1;
        const line = result.substring(lineStart, result.indexOf('\n', offset));
        return line.trim() ? match : '';
      });
      
      setFormattedCode(result.trim());
    } catch (error) {
      setFormattedCode('Error formatting code: ' + error);
    }
  };

  const getIndentation = (level: any, size: any, tabs: any) => {
    if (tabs) {
      return '\t'.repeat(level);
    }
    return ' '.repeat(level * size);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([formattedCode], { type: 'text/typescript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted-code.ts';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setInputCode('');
    setFormattedCode('');
  };

  const syntaxHighlight = (code: any) => {
    if (!code) return '';
    
    return code
      .replace(/(interface|class|function|const|let|var|type|enum|namespace|import|export|from|as|extends|implements|public|private|protected|static|readonly|async|await|return|if|else|for|while|do|switch|case|default|break|continue|try|catch|finally|throw|new|this|super|null|undefined|true|false|number|string|boolean|any|void|never|unknown)/g, 
        '<span style="color: #569cd6;">$1</span>')
      .replace(/("[^"]*"|'[^']*'|`[^`]*`)/g, 
        '<span style="color: #ce9178;">$1</span>')
      .replace(/(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, 
        '<span style="color: #6a9955;">$1</span>')
      .replace(/(\d+)/g, 
        '<span style="color: #b5cea8;">$1</span>')
      .replace(/([{}[\]().,;:])/g, 
        '<span style="color: #d4d4d4;">$1</span>');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Code2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">TypeScript Code Formatter</h1>
                <p className="text-gray-600">Format and beautify your TypeScript code with proper indentation</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Indent Size:</label>
                <select 
                  value={indentSize} 
                  onChange={(e) => setIndentSize(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value={2}>2 spaces</option>
                  <option value={4}>4 spaces</option>
                  <option value={8}>8 spaces</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useTabs"
                  checked={useTabs}
                  onChange={(e) => setUseTabs(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="useTabs" className="text-sm font-medium text-gray-700">Use tabs</label>
              </div>
              
              <button
                onClick={() => formatTypeScript(inputCode)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Format Code
              </button>
              
              <button
                onClick={handleReset}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Input Code</h2>
                <span className="text-sm text-gray-500">{inputCode.length} characters</span>
              </div>
              <textarea
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="Paste your TypeScript code here..."
                className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Formatted Code</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    disabled={!formattedCode}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={!formattedCode}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>
              <div className="relative">
                <pre className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm overflow-auto bg-gray-50">
                  <code 
                    dangerouslySetInnerHTML={{ 
                      __html: syntaxHighlight(formattedCode) || '<span class="text-gray-400">Formatted code will appear here...</span>' 
                    }}
                  />
                </pre>
              </div>
            </div>
          </div>
          
          {formattedCode && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Formatting completed successfully</span>
                <span>{formattedCode.length} characters formatted</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TypeScriptFormatter;